import type { ChangeEvent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FollowUpGroup } from '@/models/template';
import type { FollowUp } from '@/models/report';
import Panel from '@/features/report/shared/Panel/Panel';
import styles from './FollowUpPanel.module.css';

type CustomState = 'hidden' | 'editing' | 'chip';

interface FollowUpPanelProps {
  followUpGroups: FollowUpGroup[];
  value: FollowUp | null;
  onChange: (value: FollowUp | null) => void;
  disabled?: boolean;
}

export default function FollowUpPanel({
  followUpGroups,
  value,
  onChange,
  disabled = false,
}: FollowUpPanelProps) {
  const options = useMemo(
    () => followUpGroups.flatMap((g) => g.followUpLabels),
    [followUpGroups]
  );

  const [customState, setCustomState] = useState<CustomState>(() =>
    value?.customValue ? 'chip' : 'hidden'
  );
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync state when value changes externally (e.g. report load)
  useEffect(() => {
    if (value?.customValue) {
      setCustomState('chip');
    }
  }, [value?.customValue]);

  // Auto-focus when entering edit mode
  useEffect(() => {
    if (customState === 'editing') {
      inputRef.current?.focus();
    }
  }, [customState]);

  const handleClassChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const followUpClass = e.target.value || null;

      if (!followUpClass) {
        onChange(null);
      } else {
        onChange({ followUpClass, customValue: null });
      }
      // Selecting a preset clears any custom chip
      setCustomState('hidden');
    },
    [onChange]
  );

  const handleStartEditing = useCallback(() => {
    setDraft(value?.customValue ?? '');
    setCustomState('editing');
  }, [value?.customValue]);

  const commitDraft = useCallback(() => {
    const trimmed = draft.trim();

    if (trimmed) {
      // Custom value clears the dropdown preset (mutually exclusive)
      onChange({ followUpClass: null, customValue: trimmed });
      setCustomState('chip');
    } else {
      onChange(null);
      setCustomState('hidden');
    }
  }, [draft, onChange]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitDraft();
      } else if (e.key === 'Escape') {
        setCustomState(value?.customValue ? 'chip' : 'hidden');
      }
    },
    [commitDraft, value?.customValue]
  );

  const handleRemoveChip = useCallback(() => {
    setCustomState('hidden');
    onChange(null);
  }, [onChange]);

  if (options.length === 0) return null;

  return (
    <Panel title="Follow Up">
      <div className={styles.row}>
        <div className={styles.selectWrapper}>
          <select
            className={styles.select}
            aria-label="Follow Up"
            data-testid="follow-up-select"
            value={value?.followUpClass ?? ''}
            onChange={handleClassChange}
            disabled={disabled}
          >
            <option value="">No follow-up selected</option>
            {options.map((option) => (
              <option key={option.followUpClass} value={option.followUpClass}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {customState === 'hidden' && (
          <button
            type="button"
            className={styles.addCustomButton}
            data-testid="follow-up-add-custom"
            onClick={handleStartEditing}
            disabled={disabled}
          >
            + Add custom
          </button>
        )}

        {customState === 'editing' && (
          <input
            ref={inputRef}
            className={styles.customInput}
            type="text"
            aria-label="Custom follow-up"
            data-testid="follow-up-custom"
            placeholder="Custom follow-up (e.g. 3 weeks)"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitDraft}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />
        )}

        {customState === 'chip' && value?.customValue && (
          <span className={styles.chip} data-testid="follow-up-chip">
            <button
              type="button"
              className={styles.chipLabel}
              data-testid="follow-up-chip-edit"
              onClick={disabled ? undefined : handleStartEditing}
              disabled={disabled}
            >
              {value.customValue}
            </button>
            <button
              type="button"
              className={styles.chipRemove}
              data-testid="follow-up-remove-custom"
              onClick={handleRemoveChip}
              disabled={disabled}
              aria-label="Remove custom follow-up"
            >
              &times;
            </button>
          </span>
        )}
      </div>
    </Panel>
  );
}
